import { useEffect, useRef, useState } from "react";

import { cn, isNotEmpty } from "@/libs/utils";

import { ValueType } from "@/types/bid";

import { GetLiveTextSearch } from "@/services/Bid";

import { BidNoticeSuggestSearchClientList } from "@/consts/Bid";
import { DashboardSearchProjectByTitle } from "@/consts/Dashboard";

import CommonImg from "@/components/Common/Img";

import CloseIcon from "@/assets/images/close-icon.png";

type Props = {
  className?: string;
  placeholder: string;
  method: string;
  defaultValue: ValueType[] | [];
  onChange: (valueList: ValueType[]) => void;
  itemKey: string;
  boxClassName?: string;
  passEmpty?: boolean;
  debounceMs?: number;
};

const OnlyNameList = [
  BidNoticeSuggestSearchClientList,
  DashboardSearchProjectByTitle,
];

export default function LiveTextSearch({
  className,
  placeholder,
  method,
  defaultValue,
  onChange,
  itemKey,
  boxClassName,
  passEmpty = false,
  debounceMs = 100,
}: Props) {
  const [height, setHeight] = useState<number>(28);
  const ref = useRef<any>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [searchResult, setSearchResult] = useState<ValueType[]>([]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideResultTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSelectingRef = useRef<boolean>(false);

  const processBulkSearch = async (value: string) => {
    const matchText = ",";
    if (value.includes(matchText)) {
      const searchList = value
        .split(matchText)
        .map((item) => item.trim())
        .filter((s) => s.length > 0);

      const resultList = await Promise.all(
        searchList.map(async (query) => {
          try {
            const dataList = await GetLiveTextSearch(method, query);
            const result = dataList.data.pods[0].content.items;

            const exactMatch = result.find(
              (item) =>
                item[`${itemKey}_name`] === query ||
                item[`${itemKey}_id`] === query
            );
            return {
              query,
              result: exactMatch
                ? {
                    label: exactMatch[`${itemKey}_name`],
                    value: exactMatch[`${itemKey}_id`],
                  }
                : null,
            };
          } catch (error) {
            console.error(`Error fetching data for "${query}":`, error);
            return { query, result: null };
          }
        })
      );

      const matchedResultList = resultList.reduce<ValueType[]>((acc, entry) => {
        if (entry.result !== null) {
          acc.push(entry.result);
        }
        return acc;
      }, []);
      const newResultList = [
        ...defaultValue,
        ...matchedResultList.filter(
          (item) => !defaultValue.some((d) => d.label === item.label)
        ),
      ];
      onChange(newResultList);

      const unmatchedSearchList: string = resultList
        .reduce<string[]>((acc, entry) => {
          if (entry.result === null) {
            acc.push(entry.query);
          }
          return acc;
        }, [])
        .join("");
      // TODO:: 일치하지 않는 내용은 필요 없어 삭제인데 일단 주석
      // setSearchText(unmatchedSearchList);
      // return unmatchedSearchList;

      const resetText = "";
      setSearchText(resetText);
      return resetText;
    }
    return value;
  };

  const handleLiveSearch = async (value: string) => {
    const unmatchedValue = await processBulkSearch(value);
    if (!isNotEmpty(unmatchedValue) && !passEmpty) {
      setSearchResult([]);
      return;
    }
    try {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        const result = await GetLiveTextSearch(method, unmatchedValue);
        if (result.data.success) {
          setSearchResult(
            result.data.pods[0].content.items.map((item: any) => ({
              label: item[`${itemKey}_name`],
              value: item[`${itemKey}_id`],
            }))
          );
        }
      }, debounceMs);
    } catch (e) {
    } finally {
    }
  };

  const handleChange = (newSelectList: ValueType[]) => {
    onChange(newSelectList);
    setHeight(ref.current.clientHeight);
  };

  const handleResultClick = (result: ValueType) => {
    const isSelected = defaultValue.some(
      (prevItem) => prevItem.value === result.value
    );
    const newSelectList = isSelected
      ? defaultValue.filter((prevItem) => prevItem.value !== result.value)
      : [...defaultValue, result];
    handleChange(newSelectList);
    // 드롭다운을 닫지 않고 유지
  };

  // 전체 선택/해제 기능
  const isAllSelected = searchResult.length > 0 &&
    searchResult.every(item => defaultValue.some(v => v.value === item.value));

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      // 전체 해제: 검색 결과에 있는 항목들만 제거
      const searchResultValues = new Set(searchResult.map(item => item.value));
      const newSelectList = defaultValue.filter(item => !searchResultValues.has(item.value));
      handleChange(newSelectList);
    } else {
      // 전체 선택
      const existingValues = new Set(defaultValue.map(item => item.value));
      const newItems = searchResult.filter(item => !existingValues.has(item.value));
      const newSelectList = [...defaultValue, ...newItems];
      handleChange(newSelectList);
    }
  };

  const isResultSelected = (result: ValueType) => {
    return defaultValue.some((item) => item.value === result.value);
  };

  // 드롭다운 영역 클릭 시 blur 방지
  const handleDropdownMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isSelectingRef.current = true;
  };

  const handleDropdownMouseUp = () => {
    // blur 타이머보다 늦게 리셋
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 200);
  };

  const handleBlur = () => {
    // 선택 중이면 blur 무시
    if (isSelectingRef.current) {
      return;
    }

    if (hideResultTimeoutRef.current) {
      clearTimeout(hideResultTimeoutRef.current);
    }
    hideResultTimeoutRef.current = setTimeout(() => {
      setSearchResult([]);
    }, 150); // 타이밍을 조금 더 여유롭게 설정
  };

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.clientHeight);
    }
  }, [defaultValue]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (hideResultTimeoutRef.current) {
        clearTimeout(hideResultTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative flex flex-col" onBlur={handleBlur}>
      <div
        ref={ref}
        className={cn(
          "min-h-[30px] max-h-[80px] min-w-[200px] max-w-[200px] overflow-auto px-[8px] py-[4px] border-[1px] border-gray-D rounded-[4px] text-[10px] text-gray-B flex flex-col justify-start",
          className
        )}
      >
        {isNotEmpty(defaultValue) && (
          <div className="inline-flex flex-wrap gap-[4px] text-white font-bold">
            {defaultValue.map((value, valueIndex) => (
              <div
                key={valueIndex}
                className={cn(
                  "w-fit px-[6px] py-[4px] bg-dark-red flex gap-[8px] items-center rounded-[4px]",
                  boxClassName
                )}
              >
                <CommonImg
                  src={CloseIcon}
                  alt="Close"
                  className="size-[6px] cursor-pointer"
                  onClick={() => {
                    const newSelectList = defaultValue.filter(
                      (valueItem) => valueItem.value !== value.value
                    );
                    handleChange(newSelectList);
                  }}
                />
                <span className="w-fit max-w-[160px] whitespace-nowrap overflow-hidden overflow-ellipsis">
                  {value.label}
                  {!OnlyNameList.includes(method) && `(${value.value})`}
                </span>
              </div>
            ))}
          </div>
        )}
        <input
          type="text"
          className="w-full pt-[4px] focus:outline-none placeholder:text-gray-C"
          placeholder={isNotEmpty(defaultValue) ? "" : placeholder}
          value={searchText}
          onFocus={(e) => {
            // focus 시 hideResult 타이머 취소
            if (hideResultTimeoutRef.current) {
              clearTimeout(hideResultTimeoutRef.current);
            }
            handleLiveSearch(e.target.value);
          }}
          onChange={(e) => {
            const value = e.target.value;
            setSearchText(value);
            handleLiveSearch(value);
          }}
          onClick={() => {
            const value = "";
            setSearchText(value);
            handleLiveSearch(value);
          }}
          onKeyDown={(e) => {
            // 입력창에서 Backspace 눌렀을 때 선택 항목 삭제
            if (
              e.key === "Backspace" &&
              searchText === "" &&
              defaultValue.length > 0
            ) {
              handleChange(defaultValue.slice(0, -1));
            }
          }}
        />
      </div>
      {searchResult.length > 0 && (
        <div
          className={cn(
            "max-h-[150px] w-full overflow-auto absolute z-10 bg-white p-[8px] flex flex-col gap-[4px] border-x-[1px] border-b-[1px] rounded-b-[4px] text-[10px] font-bold"
          )}
          style={{
            top: `${height + 2}px`,
          }}
          onMouseDown={handleDropdownMouseDown}
          onMouseUp={handleDropdownMouseUp}
        >
          {/* 전체 선택/해제 버튼 */}
          <div
            className="p-[4px] mb-1 rounded-[4px] cursor-pointer text-center border-b border-gray-200 text-white hover:opacity-90"
            style={{ backgroundColor: "rgb(167, 109, 109)" }}
            onClick={handleSelectAllToggle}
          >
            {isAllSelected ? "전체 해제" : `전체 선택 (${searchResult.length}개)`}
          </div>
          {searchResult.map((result, resultIndex) => (
            <label
              key={resultIndex}
              className="relative p-[4px] hover:bg-gray-D rounded-[4px] cursor-pointer pointer-events-auto flex items-center gap-2"
            >
              <input
                type="checkbox"
                checked={isResultSelected(result)}
                onChange={() => handleResultClick(result)}
                className="w-3 h-3 cursor-pointer"
              />
              <span>
                {result.label}
                {!OnlyNameList.includes(method) && `(${result.value})`}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
